import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Pagination component for table navigation
 */
const Pagination = ({
  currentPage = 1,
  totalPages = 1,
  totalItems = 0,
  itemsPerPage = 10,
  onPageChange = () => {},
  isLoading = false,
}) => {
  const safeTotalPages = Math.max(1, totalPages || 1);
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems || itemsPerPage);

  const handlePrevious = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < safeTotalPages) {
      onPageChange(currentPage + 1);
    }
  };

  const handlePageClick = (page) => {
    if (page !== currentPage) {
      onPageChange(page);
    }
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(safeTotalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    if (startPage > 1) {
      pages.push(1);
      if (startPage > 2) {
        pages.push('...');
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    if (endPage < safeTotalPages) {
      if (endPage < safeTotalPages - 1) {
        pages.push('...');
      }
      pages.push(safeTotalPages);
    }

    return pages;
  };

  const pageNumbers = getPageNumbers();

  return (
    <div className="flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-0 px-4 md:px-6 py-4">
      {/* Left side: Item count - hidden on mobile */}
      <div className="hidden md:block text-sm" style={{ color: "#000000" }}>
        Showing <span className="font-semibold" style={{ color: "#000000" }}>{startItem}</span> to{' '}
        <span className="font-semibold" style={{ color: "#000000" }}>{endItem}</span> of{' '}
        <span className="font-semibold" style={{ color: "#000000" }}>{totalItems || safeTotalPages * itemsPerPage}</span> results
      </div>

      {/* Right side: Page label + controls */}
      <div className="w-full md:w-auto md:ml-auto flex items-center justify-between md:justify-end gap-3">
        <span className="text-xs md:text-xs" style={{ color: "#000000" }}>
          Page <span className="font-medium" style={{ color: "#000000" }}>{currentPage}</span> of{' '}
          <span className="font-medium" style={{ color: "#000000" }}>{safeTotalPages}</span>
        </span>

        {/* Page buttons */}
        <div className="flex items-center gap-2 rounded-lg p-2">
          {/* Previous button */}
          <button
            onClick={handlePrevious}
            disabled={currentPage === 1}
            className="p-2 rounded hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-gray-400 hover:text-gray-200"
            aria-label="Previous page"
            title="Previous page"
          >
            <ChevronLeft size={18} />
          </button>

          {/* Page numbers - only on desktop */}
          <div className="hidden md:flex items-center gap-1 px-2">
            {pageNumbers.map((page, index) => (
              page === '...' ? (
                <span key={`ellipsis-${index}`} className="px-2 font-medium" style={{ color: "#000000" }}>
                  ...
                </span>
              ) : (
                <button
                  key={page}
                  onClick={() => handlePageClick(page)}
                  disabled={page > safeTotalPages || isLoading}
                  className={`px-3 py-1 rounded text-sm font-medium transition-all ${
                    page === currentPage
                      ? 'border font-semibold'
                      : page > safeTotalPages || isLoading
                        ? 'cursor-not-allowed opacity-50'
                        : 'cursor-pointer'
                  }`}
                  style={{
                    color: page === currentPage ? '#000000' : '#000000',
                    borderColor: page === currentPage ? '#000000' : 'transparent'
                  }}
                  aria-label={`Go to page ${page}`}
                  aria-current={page === currentPage ? 'page' : undefined}
                >
                  {page}
                </button>
              )
            ))}
          </div>

          {/* Next button */}
          <button
            onClick={handleNext}
            disabled={currentPage === safeTotalPages}
            className="p-2 rounded hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-gray-400 hover:text-gray-200"
            aria-label="Next page"
            title="Next page"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Pagination;
